/**
 * Trip invitation reads and responses.
 *
 * Backed by `GET /v1/trips/invitations/preview` and
 * `POST /v1/trips/invitations/respond`, both public — the signed token in the
 * email link is the credential. When a session cookie happens to be present the
 * respond endpoint also links the accepting account to the trip member row,
 * which is why every call from here is credentialed.
 */
import { z } from "zod/v4";

import { apiGet, apiPost } from "@/lib/apiClient";

/** Matches the API's `respondInvitationBodySchema`. */
export const invitationActionSchema = z.enum(["accept", "decline"]);
export type InvitationAction = z.infer<typeof invitationActionSchema>;

/**
 * Search params on the landing page the invitation emails link to:
 * `${APP_URL}/trips/invitations/respond?token=...&action=accept`
 *
 * Both are optional at the type level so a mangled link renders a real error
 * page rather than throwing inside the router.
 */
export const invitationSearchSchema = z.object({
  token: z.string().min(1).optional().catch(undefined),
  action: invitationActionSchema.optional().catch(undefined),
});
export type InvitationSearch = z.infer<typeof invitationSearchSchema>;

/**
 * Matches the API's `previewInvitationResponseSchema` — a read-only projection
 * of the invitation, holding only what this page renders. It deliberately says
 * nothing about the trip's contents or the other people on it, so treat any
 * additional field appearing here as a mistake rather than an opportunity.
 *
 * `status` is derived server-side: a pending invitation whose expiry has passed
 * reads as `EXPIRED` without anything being written, so the page can show the
 * dead-end state without POSTing to discover it.
 */
export const invitationPreviewSchema = z.object({
  type: z.enum(["MEMBER", "OWNERSHIP_TRANSFER"]),
  status: z.enum(["PENDING", "ACCEPTED", "DECLINED", "CANCELLED", "EXPIRED"]),
  tripName: z.string(),
  inviterName: z.string().nullable(),
  role: z.enum(["MANAGER", "CONTRIBUTOR", "VIEWER", "LIMITED_VIEWER"]).nullable(),
  expiresAt: z.string(),
});
export type InvitationPreview = z.infer<typeof invitationPreviewSchema>;

export async function fetchInvitationPreview(token: string): Promise<InvitationPreview> {
  const body = await apiGet<unknown>(
    `/trips/invitations/preview?token=${encodeURIComponent(token)}`,
  );
  return invitationPreviewSchema.parse(body);
}

/** Matches the API's `respondInvitationResponseSchema`. */
export type RespondInvitationResponse = {
  message: string;
  tripId?: string;
};

export function respondToInvitation(input: {
  token: string;
  action: InvitationAction;
}): Promise<RespondInvitationResponse> {
  return apiPost<RespondInvitationResponse>("/trips/invitations/respond", input);
}
